import React, { useState, useMemo } from 'react';
import { Button, Collapse, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Add as AddIcon, PersonAdd as PersonAddIcon, UploadFile as ImportIcon, Phone as PhoneIcon, Mail as MailIcon, Schedule as ScheduleIcon, CalendarMonth as CalendarIcon, Restore as RestoreIcon, Bookmark as BookmarkIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, FileDownload as ExportIcon, ViewColumn as ColumnIcon } from '@mui/icons-material';
import { formatBudgetRange } from '../../utils/helper';
import {
  LEAD_THEME as T,
  getStageConfig as STAGE_CFG,
  getStatusConfig as STATUS_CFG,
  ALL_STAGES,
  DEFAULT_VISIBLE_COLUMNS,
  formatLeadDateTime as formatDateTime,
  formatLeadDate as fmtDate,
  getDaysSince as daysSince,
  formatStatusLabel,
  isClosureStageLead,
} from '../../utils/lead_helper_v2';
import {
  SmartTableTH,
  SmartTableTD,
  TableLoadingState,
  TableEmptyState,
  TABLE_ROW_GLOBAL_CSS,
  TABLE_ROW_CLASS,
  TABLE_ROW_SELECTED_CLASS,
} from '../../components/common/SmartTable';
import { ALL_COLUMNS } from './components/LeadsTableV2';
import LeadSidebarV2 from './LeadSidebarV2';
import LeadsActionMenu from '../../components/common/LeadsActionMenu';
import Pagination from '../../components/common/Pagination';

// ─── Sidebar Stage Item ──────────────────────────────────────────────────────
function SidebarStageItem({ stage, label, count, active, onClick }) {
  const cfg = STAGE_CFG(stage);
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', cursor: 'pointer', borderRadius: 8, margin: '1px 6px',
        background: active ? T.greenSf : 'transparent',
        borderLeft: active ? `3px solid ${T.green}` : '3px solid transparent',
        transition: 'all 120ms',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot }} />
        <span style={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? T.green : T.ink2 }}>
          {label}
        </span>
      </div>
      <span style={{
        fontSize: 11, fontWeight: 700,
        color: active ? T.green : T.muted,
        background: active ? T.greenLt : T.bg,
        borderRadius: 99, padding: '1px 7px', minWidth: 22, textAlign: 'center',
      }}>{count}</span>
    </div>
  );
}

// ─── Stage Pill ──────────────────────────────────────────────────────────────
function StagePill({ stage, label }) {
  const cfg = STAGE_CFG(stage);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: cfg.bg, color: cfg.text,
      fontSize: 11, fontWeight: 700, padding: '3px 9px 3px 7px',
      borderRadius: 99, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dot }} />
      {label || stage}
    </span>
  );
}

// ─── Status Pill (funnel sub-state, matches Stage visual language) ────────────
function StatusPill({ status, label }) {
  const cfg = STATUS_CFG(status);
  const displayLabel = label || formatStatusLabel(status) || '—';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      background: cfg.bg, color: cfg.text,
      fontSize: 11, fontWeight: 600, padding: '3px 9px 3px 7px',
      borderRadius: 99, whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: cfg.dot, flexShrink: 0 }} />
      {displayLabel}
    </span>
  );
}

// ─── Owner pill (Sales Owner column; matches Stage/Status pill style) ───────────
function OwnerChip({ name, assigned, onClick }) {
  const isUnassigned = !assigned || !name || name === 'Unassigned';
  const displayText = isUnassigned ? 'Unassigned' : (name || '—');
  return (
    <span
      role={typeof onClick === 'function' ? 'button' : undefined}
      tabIndex={typeof onClick === 'function' ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => { if (typeof onClick === 'function' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onClick(); } }}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        background: isUnassigned ? T.borderL : T.greenSf,
        color: isUnassigned ? T.muted : T.ink2,
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 9px 3px 7px',
        borderRadius: 99,
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: typeof onClick === 'function' ? 'pointer' : 'default',
        border: `1px solid ${isUnassigned ? T.border : 'transparent'}`,
        transition: 'background 0.15s, border-color 0.15s',
      }}
      onMouseEnter={(e) => {
        if (typeof onClick !== 'function') return;
        e.currentTarget.style.background = isUnassigned ? '#e8eeeb' : T.greenLt;
        e.currentTarget.style.borderColor = isUnassigned ? T.muted : T.green;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = isUnassigned ? T.borderL : T.greenSf;
        e.currentTarget.style.borderColor = isUnassigned ? T.border : 'transparent';
      }}
    >
      <span style={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        flexShrink: 0,
        background: isUnassigned ? T.muted : T.green,
      }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayText}</span>
    </span>
  );
}

// ─── Table header / cell ─────────────────────────────────────────────────────
// ─── Main Component ──────────────────────────────────────────────────────────
const LeadsListViewV2 = ({
  leads: leadsProp,
  selectedLead,
  onSelectLead,
  selectedLeads: selectedLeadsProp,
  onSelectAll: onSelectAllProp,
  onSelectLead: onSelectLeadProp,
  page = 1,
  rowsPerPage = 50,
  totalCount = 0,
  totalPages = 1,
  onPageChange,
  onRowsPerPageChange,
  hideTablePagination = false,
  onCreateLead,
  onImport,
  onSourceUpdate,
  isLoading = false,
  statusCounts: statusCountsFromApi = {},
  activeStageFilter = 'all',
  onStageFilterChange,
  onExport,
  onColumnCustomize,
  onAction,
  visibleColumns: visibleColumnsProp = DEFAULT_VISIBLE_COLUMNS,
  isDeleted = false,
  savedSearches = [],
  onApplySavedSearch,
  activeSavedSearchId = null,
  activeSourceFilter = null,
  onSourceFilterChange,
  defaultFilters = {},
  hideSidebar = false,
}) => {
  const leads = Array.isArray(leadsProp) ? leadsProp : [];

  const [internalSelected, setInternalSelected] = useState([]);
  const [internalPage, setInternalPage] = useState(1);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(15);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [savedSearchesExpanded, setSavedSearchesExpanded] = useState(false);
  const [createMenuAnchorEl, setCreateMenuAnchorEl] = useState(null);

  const controlled = selectedLeadsProp != null && typeof onSelectLeadProp === 'function' && typeof onSelectAllProp === 'function';
  const selected = controlled ? selectedLeadsProp : internalSelected;

  const serverSide = typeof onPageChange === 'function' && typeof totalCount === 'number';

  const visibleColumns = Array.isArray(visibleColumnsProp) && visibleColumnsProp.length > 0 ? visibleColumnsProp : DEFAULT_VISIBLE_COLUMNS;
  const columns = useMemo(
    () => ALL_COLUMNS.filter((c) => visibleColumns.includes(c.id)),
    [visibleColumns]
  );

  // Source Funnel: only from status-counts API. When the pipeline slice has no leads, API returns
  // source: [] — we must not fall back to defaultFilters (that showed every source with 0).
  const sourceOptions = useMemo(() => {
    const apiSources = statusCountsFromApi?.source;
    if (!Array.isArray(apiSources)) {
      return [];
    }
    return apiSources
      .filter((item) => Number(item.count ?? 0) > 0)
      .map((item) => ({
        value: item.value ?? item.label ?? '',
        label: item.label ?? item.value ?? '',
        count: Number(item.count ?? 0),
      }));
  }, [statusCountsFromApi?.source]);

  // Search is handled in the top toolbar; no client-side filter here
  const filtered = useMemo(() => leads, [leads]);

  const currentRPP = serverSide ? rowsPerPage : internalRowsPerPage;
  const clientTotalPages = Math.max(1, Math.ceil(filtered.length / currentRPP));
  const currentPage = serverSide ? page : internalPage;
  const paginated = serverSide ? filtered : filtered.slice((currentPage - 1) * currentRPP, currentPage * currentRPP);

  const paginatedSelectable = useMemo(
    () => paginated.filter((l) => !isClosureStageLead(l)),
    [paginated]
  );
  const selectableCount = paginatedSelectable.length;
  const selectableIdsOnPage = useMemo(
    () => new Set(paginatedSelectable.map((l) => l.id)),
    [paginatedSelectable]
  );
  const selectedBulkEligibleOnPage = useMemo(
    () => selected.filter((id) => selectableIdsOnPage.has(id)),
    [selected, selectableIdsOnPage]
  );
  const allSelectableSelected =
    selectableCount > 0 && selectedBulkEligibleOnPage.length === selectableCount;

  const toggleSelect = (id) => {
    const row = paginated.find((l) => l.id === id);
    if (row && isClosureStageLead(row)) return;
    if (controlled) onSelectLeadProp(id);
    else setInternalSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };
  const toggleAll = () => {
    if (controlled) {
      onSelectAllProp(!allSelectableSelected);
    } else {
      const ids = paginatedSelectable.map((l) => l.id);
      const allSelected = ids.length > 0 && ids.every((id) => internalSelected.includes(id));
      setInternalSelected((s) =>
        allSelected ? s.filter((id) => !ids.includes(id)) : [...new Set([...s, ...ids])]
      );
    }
  };

  const totalDisplay = serverSide ? totalCount : filtered.length;
  const totalPagesDisplay = serverSide ? totalPages : clientTotalPages;

  const renderCell = (columnId, l) => {
    switch (columnId) {
      case 'lead_id':
        return (
          <a onClick={(e) => e.stopPropagation()} href={`/leads/${l.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontSize: 12.5, fontWeight: 700, color: T.green }}>
            #{l.lead_id || l.id}
          </a>
        );
      case 'name':
        return (
          <a onClick={(e) => e.stopPropagation()} href={`/leads/${l.id}`} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', fontSize: 12.5, fontWeight: 600, color: T.green, display: 'block' }}>
            {l.contact?.name || l.name || '—'}
          </a>
        );
      case 'email':
        return (
          <span style={{ fontSize: 11, color: T.subtle }}>{l.contact?.email || '—'}</span>
        );
      case 'mobile':
        return (
          <span style={{ fontSize: 12.5, color: l.contact?.mobile ? T.ink2 : T.muted }}>{l.contact?.mobile || '—'}</span>
        );
      case 'created_at':
        return (
          <span style={{ fontSize: 12.5, color: T.ink2, fontWeight: 500 }}>{l.created_at ? formatDateTime(l.created_at) : '—'}</span>
        );
      case 'first_contact_at':
        return <span style={{ fontSize: 11, color: T.subtle }}>{l.first_contact_at ? formatDateTime(l.first_contact_at) : 'Never'}</span>;
      case 'last_contacted_at': {
        const lastCt = l.last_contact_at || l.last_contacted_at;
        return (
          <span style={{ fontSize: 12.5, color: lastCt ? T.ink2 : T.muted }}>
            {lastCt ? formatDateTime(lastCt) : 'Never'}
          </span>
        );
      }
      case 'lead_age':
        return <span style={{ fontSize: 12, fontWeight: 500 }}>{l.lead_age_days != null ? `${l.lead_age_days}d` : '—'}</span>;
      case 'time_since_last_contact':
        return (
          <span style={{ fontSize: 12, fontWeight: l.days_since_last_contact > 30 ? 700 : 400, color: l.days_since_last_contact > 30 ? '#b91c1c' : l.days_since_last_contact > 7 ? '#b45309' : T.subtle }}>
            {l.days_since_last_contact != null ? `${l.days_since_last_contact}d` : '—'}
          </span>
        );
      case 'stage':
        return <StagePill stage={l.lead_stage || l.stage || 'new'} label={l.stage_verbose || (l.stage && l.stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))} />;
      case 'status':
        return (
          <StatusPill
            status={l.status}
            label={l.status_verbose}
          />
        );
      case 'sales_owner': {
        const ownerName = l.assigned_agent?.name || l.agent?.name;
        const assigned = Boolean(ownerName);
        return (
          <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
            <OwnerChip
              name={ownerName || 'Unassigned'}
              assigned={assigned}
              onClick={() => {
                if (typeof onAction !== 'function') return;
                onAction('assignAgent', l);
              }}
            />
          </div>
        );
      }
      case 'is_reassigned':
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: l.is_reassigned ? '#fef3c7' : T.borderL, color: l.is_reassigned ? '#92400e' : T.subtle }}>
            {l.is_reassigned ? 'Yes' : 'No'}
          </span>
        );
      case 'next_followup':
        return l.next_followup?.datetime ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.ink2 }}>
            <ScheduleIcon sx={{ fontSize: 12, color: T.green }} />
            {formatDateTime(l.next_followup.datetime)}
          </span>
        ) : <span style={{ fontSize: 11, color: T.muted }}>—</span>;
      case 'next_visit':
        return l.next_visit?.datetime ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.ink2 }}>
            <CalendarIcon sx={{ fontSize: 12, color: T.green }} />
            {formatDateTime(l.next_visit.datetime)}
          </span>
        ) : <span style={{ fontSize: 11, color: T.muted }}>—</span>;
      case 'last_completed_visit':
        return l.last_completed_visit ? (
          <span style={{ fontSize: 11, color: T.ink2 }}>{formatDateTime(l.last_completed_visit)}</span>
        ) : <span style={{ fontSize: 11, color: T.muted }}>—</span>;
      case 'site_visit_count':
        return (
          <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: (l.site_visit_count || 0) > 0 ? T.greenLt : T.borderL, color: (l.site_visit_count || 0) > 0 ? T.green : T.subtle }}>
            {l.site_visit_count ?? 0}
          </span>
        );
      case 'last_note': {
        const noteText = l.last_note?.note || '';
        return (
          <Tooltip title={noteText || ''} arrow placement="top" disableHoverListener={!noteText}>
            <span style={{
              fontSize: 11, color: noteText ? T.subtle : T.muted,
              display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
              overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180, whiteSpace: 'normal',
            }}>
              {noteText || '—'}
            </span>
          </Tooltip>
        );
      }
      case 'all_notes':
        return (
          <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: (l.notes_count || 0) > 0 ? T.greenSf : T.borderL, color: T.ink2 }}>
            {l.notes_count ?? 0}
          </span>
        );
      case 'total_connected_calls':
        return (
          <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: (l.total_connected_calls || 0) > 0 ? '#dbeafe' : T.borderL, color: T.ink2 }}>
            {l.total_connected_calls ?? 0}
          </span>
        );
      case 'total_talktime':
        return <span style={{ fontSize: 12, fontWeight: 500 }}>{l.total_talktime_formatted || '0m'}</span>;
      case 'location':
        return <span style={{ fontSize: 12, color: l.location ? T.ink2 : T.muted }}>{l.location || '—'}</span>;
      case 'source':
        return <span style={{ fontSize: 12, color: T.subtle }}>{l.source_verbose || l.source || '—'}</span>;
      case 'tags':
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
            {l.tags?.length ? (
              <>
                {l.tags.map((tag, idx) => (
                  <span key={idx} style={{ padding: '2px 6px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: '#7E57C2', color: '#fff' }}>
                    {typeof tag === 'string' ? tag : (tag.name || tag)}
                  </span>
                ))}
              </>
            ) : <span style={{ fontSize: 11, color: T.muted }}>—</span>}
          </div>
        );
      case 'channel_partner':
        return <span style={{ fontSize: 12, color: T.subtle }}>{l.channel_partner?.name || '—'}</span>;
      case 'budget': {
        const budgetStr = l.budget ? formatBudgetRange(l.budget) : '';
        const display = budgetStr && budgetStr !== 'Not specified' ? budgetStr : '—';
        return (
          <span style={{ fontSize: 12.5, fontWeight: 600, color: display !== '—' ? T.ink2 : T.muted }}>
            {display}
          </span>
        );
      }
      case 'lead_metadata':
        return (
          <span style={{ display: 'inline-flex', padding: '2px 8px', borderRadius: 99, fontSize: 11, background: l.metadata && Object.keys(l.metadata).length > 0 ? T.greenSf : T.borderL, color: T.subtle }}>
            {l.metadata ? `${Object.keys(l.metadata).length} items` : 'None'}
          </span>
        );
      case 'not_interested_reason':
        return <span style={{ fontSize: 11, color: l.lost_reason_verbose ? '#b91c1c' : T.muted }}>{l.lost_reason_verbose || '—'}</span>;
      default:
        return <span style={{ fontSize: 12, color: T.muted }}>—</span>;
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      fontFamily: "'DM Sans','Plus Jakarta Sans',sans-serif",
      background: T.bg, borderRadius: 14, overflow: 'hidden',
      border: `1.5px solid ${T.border}`,
      boxShadow: '0 2px 24px rgba(0,0,0,0.05)',
    }}>

      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflowX: 'hidden' }}>
        {/* ── SIDEBAR ─────────────────────────────────────────────────── */}
        {!hideSidebar && (
          <div style={{
            width: 210, flexShrink: 0, background: T.sidebar,
            borderRight: `1.5px solid ${T.border}`,
            display: 'flex', flexDirection: 'column',
            overflowY: 'auto',
            minHeight: 0,
          }}>
            {/* Single scrollable region: Pipeline → Source Funnel → Saved Searches */}
            <LeadSidebarV2
              embedded
              width={210}
              statusCounts={statusCountsFromApi}
              selectedStatus={activeStageFilter || 'all'}
              onStatusChange={onStageFilterChange}
            />

            {sourceOptions.length > 0 && (
              <>
                <div style={{ margin: '4px 14px 6px', borderTop: `1px solid ${T.border}` }} />

                {/* Source Funnel (expandable) — only sources with count &gt; 0 for current pipeline slice */}
                <div>
                  <div
                    onClick={() => setSourcesExpanded((e) => !e)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 16px 6px', cursor: 'pointer', borderRadius: 6, margin: '0 6px',
                    }}
                  >
                    <div style={{ fontSize: 10.5, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                      Source Funnel
                    </div>
                    {sourcesExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: T.muted }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: T.muted }} />}
                  </div>
                  <Collapse in={sourcesExpanded} timeout="auto">
                    <div>
                      {sourceOptions.map((opt) => {
                        const value = opt.value ?? opt.label;
                        const label = opt.label ?? opt.value;
                        const isSourceSelected = activeSourceFilter != null && (String(activeSourceFilter).trim() === String(value).trim() || String(activeSourceFilter).trim() === String(label).trim());
                        const count = Number(opt.count ?? 0);
                        return (
                          <div
                            key={value}
                            role="button"
                            tabIndex={0}
                            onClick={() => typeof onSourceFilterChange === 'function' && (isSourceSelected ? onSourceFilterChange(null) : onSourceFilterChange(value))}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (typeof onSourceFilterChange === 'function') (isSourceSelected ? onSourceFilterChange(null) : onSourceFilterChange(value)); } }}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '5px 14px', margin: '0 6px', borderRadius: 6,
                              cursor: typeof onSourceFilterChange === 'function' ? 'pointer' : 'default',
                              backgroundColor: isSourceSelected ? 'rgba(0, 112, 243, 0.08)' : 'transparent',
                            }}
                          >
                            <span style={{ fontSize: 12, color: isSourceSelected ? T.accent ?? '#0070F3' : T.subtle, fontWeight: isSourceSelected ? 600 : 400 }}>{label}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, color: isSourceSelected ? (T.accent ?? '#0070F3') : T.muted }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </Collapse>
                </div>
              </>
            )}

            {/* Saved Searches (expandable) */}
            {Array.isArray(savedSearches) && savedSearches.length > 0 && (
              <>
                <div style={{ margin: '4px 14px 6px', borderTop: `1px solid ${T.border}` }} />
                <div>
                  <div
                    onClick={() => {
                      setSavedSearchesExpanded((prev) => {
                        const next = !prev;
                        if (next) setSourcesExpanded(false);
                        return next;
                      });
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 16px 6px', cursor: 'pointer', borderRadius: 6, margin: '0 6px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <BookmarkIcon sx={{ fontSize: 14, color: T.muted }} />
                      <div>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
                          Saved Searches
                        </div>
                      </div>
                    </div>
                    {savedSearchesExpanded ? <ExpandLessIcon sx={{ fontSize: 18, color: T.muted }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: T.muted }} />}
                  </div>
                  <Collapse in={savedSearchesExpanded} timeout="auto">
                    <div>
                      {savedSearches.map((searchItem) => {
                        const isActive = activeSavedSearchId != null && String(activeSavedSearchId) === String(searchItem.id);
                        const count = searchItem.count ?? searchItem.lead_count ?? 0;
                        return (
                          <div
                            key={searchItem.id}
                            onClick={() => typeof onApplySavedSearch === 'function' && onApplySavedSearch(searchItem)}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '8px 14px', cursor: 'pointer', borderRadius: 8, margin: '1px 6px',
                              background: isActive ? T.greenSf : 'transparent',
                              borderLeft: isActive ? `3px solid ${T.green}` : '3px solid transparent',
                              transition: 'all 120ms',
                            }}
                          >
                            <span style={{ fontSize: 12.5, fontWeight: isActive ? 700 : 500, color: isActive ? T.green : T.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {searchItem.name || 'Unnamed'}
                            </span>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: isActive ? T.green : T.muted,
                              background: isActive ? T.greenLt : T.bg,
                              borderRadius: 99,
                              padding: '1px 7px',
                              minWidth: 22,
                              textAlign: 'center',
                              flexShrink: 0,
                            }}>
                              {count}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Collapse>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── MAIN PANEL ──────────────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflowX: 'hidden' }}>

          {/* Toolbar: title/count (left), Column customizer, Export, Create Lead (right) */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 18px', background: T.white, borderBottom: `1.5px solid ${T.border}`,
            gap: 12, flexWrap: 'nowrap', minWidth: 0,
          }}>
            <div style={{ flexShrink: 0 }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: T.ink, letterSpacing: '-0.3px' }}>
                {(() => {
                  // If a saved search is active, prefer its name for the title
                  const activeSearch =
                    activeSavedSearchId != null &&
                    Array.isArray(savedSearches)
                      ? savedSearches.find((s) => String(s.id) === String(activeSavedSearchId))
                      : null;

                  if (activeSearch && activeSearch.name) {
                    return activeSearch.name;
                  }

                  if (activeStageFilter !== 'all') {
                    return (
                      statusCountsFromApi[activeStageFilter]?.label ||
                      activeStageFilter
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, (c) => c.toUpperCase())
                    );
                  }

                  return 'All Leads';
                })()}
              </span>
              <span style={{ fontSize: 12, color: T.muted, marginLeft: 6 }}>
                · {totalDisplay} lead{totalDisplay !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Right: Column customizer, Export, Create Lead */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {typeof onColumnCustomize === 'function' && (
                <Tooltip title="Customize columns">
                  <IconButton size="small" onClick={onColumnCustomize} sx={{ color: T.ink2 }}>
                    <ColumnIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {typeof onExport === 'function' && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ExportIcon />}
                  onClick={onExport}
                  disabled={totalDisplay === 0}
                  sx={{
                    minWidth: 90,
                    height: 32,
                    borderRadius: 2,
                    fontWeight: 600,
                    borderColor: T.green,
                    color: T.green,
                    '&:hover': { borderColor: T.green, bgcolor: T.greenSf },
                  }}
                >
                  Export
                </Button>
              )}
              {(onCreateLead || onImport || onSourceUpdate) && (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                    onClick={(e) => setCreateMenuAnchorEl(e.currentTarget)}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: 18, ml: 0.25 }} />}
                    sx={{
                      height: 32,
                      minWidth: 130,
                      borderRadius: 2,
                      fontSize: '0.8125rem',
                      fontWeight: 700,
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #0a6b3e, #0d8a50)',
                      boxShadow: '0 2px 10px rgba(10,107,62,0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #085a32, #0a6b3e)',
                        boxShadow: '0 4px 12px rgba(10,107,62,0.3)',
                      },
                    }}
                  >
                    Create Lead
                  </Button>
                  <Menu
                    anchorEl={createMenuAnchorEl}
                    open={Boolean(createMenuAnchorEl)}
                    onClose={() => setCreateMenuAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                      paper: {
                        sx: {
                          minWidth: 220,
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                          mt: 1.5,
                        },
                      },
                    }}
                    MenuListProps={{ sx: { py: 0.5 } }}
                  >
                    {onCreateLead && (
                      <MenuItem
                        onClick={() => {
                          setCreateMenuAnchorEl(null);
                          onCreateLead();
                        }}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <PersonAddIcon fontSize="small" sx={{ color: T.green }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Add single lead"
                          secondary="Enter details manually"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: T.muted }}
                        />
                      </MenuItem>
                    )}
                    {onImport && (
                      <MenuItem
                        onClick={() => {
                          setCreateMenuAnchorEl(null);
                          onImport();
                        }}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <ImportIcon fontSize="small" sx={{ color: T.green }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Import from CSV"
                          secondary="Bulk add leads from file"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: T.muted }}
                        />
                      </MenuItem>
                    )}
                    {onSourceUpdate && (
                      <MenuItem
                        onClick={() => {
                          setCreateMenuAnchorEl(null);
                          onSourceUpdate();
                        }}
                        sx={{ py: 1.25 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <ImportIcon fontSize="small" sx={{ color: T.green }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Update lead source (CSV)"
                          secondary="Bulk update source column from file"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: T.muted }}
                        />
                      </MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </div>
          </div>

          {/* Table */}
          <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'auto' }}>
            {isLoading ? (
              <TableLoadingState message="Loading leads…" theme={T} />
            ) : (
              <>
                <style>{TABLE_ROW_GLOBAL_CSS}</style>
                <table className="smart-table-wrap" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}>
                  <thead>
                    <tr>
                      <SmartTableTH theme={T} width={36} style={{ paddingLeft: 16 }}>
                        <input
                          type="checkbox"
                          checked={allSelectableSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                selectedBulkEligibleOnPage.length > 0 && !allSelectableSelected;
                            }
                          }}
                          onChange={toggleAll}
                          disabled={selectableCount === 0}
                          style={{ width: 14, height: 14 }}
                        />
                      </SmartTableTH>
                      {columns.map((c) => (
                        <SmartTableTH key={c.id} theme={T} width={c.minWidth || 90}>{c.label}</SmartTableTH>
                      ))}
                      <SmartTableTH theme={T} width={60}>Actions</SmartTableTH>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.length === 0 ? (
                      <TableEmptyState
                        message="No leads found"
                        description="Try adjusting your filters or search query."
                        theme={T}
                        colSpan={columns.length + 2}
                      />
                    ) : paginated.map((l) => {
                      const isSel = selected.includes(l.id);
                      const closureRow = isClosureStageLead(l);
                      return (
                        <tr
                          key={l.id}
                          className={`${TABLE_ROW_CLASS}${isSel ? ` ${TABLE_ROW_SELECTED_CLASS}` : ''}`}
                          onDoubleClick={(e) => {
                            if (e.target?.closest?.('[data-action-cell]')) return;
                            if (!isDeleted) window.open(`/leads/${l.id}`, '_blank');
                          }}
                        >
                          <td style={{ padding: '10px 0 10px 16px', borderBottom: `1px solid ${T.borderL}`, verticalAlign: 'middle', background: T.bodyBg ?? T.white }}>
                            <input
                              type="checkbox"
                              checked={isSel}
                              disabled={closureRow}
                              onChange={() => toggleSelect(l.id)}
                              style={{ width: 14, height: 14, opacity: closureRow ? 0.45 : 1 }}
                              onClick={(e) => e.stopPropagation()}
                              title={closureRow ? 'Closure leads cannot be included in bulk actions' : undefined}
                            />
                          </td>
                          {columns.map((c) => (
                            <SmartTableTD key={c.id} theme={T} wrap={c.id === 'tags' || c.id === 'last_note'}>{renderCell(c.id, l)}</SmartTableTD>
                          ))}
                          <td data-action-cell style={{ padding: '10px 12px', borderBottom: `1px solid ${T.borderL}`, verticalAlign: 'middle', background: T.bodyBg ?? T.white }} onClick={(e) => e.stopPropagation()}>
                            {!isDeleted ? (
                              typeof onAction === 'function' ? (
                                <LeadsActionMenu
                                  onAddNote={() => onAction('notes', l)}
                                  onUpdateStatus={() => onAction('status', l)}
                                  onView={() => { window.open(`/leads/${l.id}`, '_blank'); }}
                                  onDelete={() => onAction('delete', l)}
                                  onShareProperties={() => onAction('shareProperties', l)}
                                  disabled={isDeleted}
                                  currentStage={l.stage}
                                />
                              ) : null
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                startIcon={<RestoreIcon sx={{ fontSize: 16 }} />}
                                onClick={() => onAction && onAction('restore', l)}
                                sx={{ fontSize: '0.75rem', textTransform: 'none', borderRadius: '4px', py: 0.5, px: 1, minWidth: 'auto' }}
                              >
                                Restore
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
          </div>

          {/* Pagination */}
          {!hideTablePagination && (totalDisplay > 0) && (
            <div style={{ borderTop: `1px solid ${T.border}`, background: '#f8fbf9', padding: '6px 12px' }}>
              <Pagination
                totalCount={totalDisplay}
                currentPage={currentPage}
                pageSize={currentRPP}
                totalPages={totalPagesDisplay}
                onPageChange={(nextPage) => {
                  if (serverSide && typeof onPageChange === 'function') onPageChange(nextPage);
                  else setInternalPage(nextPage);
                }}
                onPageSizeChange={(size) => {
                  if (serverSide && typeof onRowsPerPageChange === 'function') onRowsPerPageChange(size);
                  else { setInternalRowsPerPage(size); setInternalPage(1); }
                }}
                rowsPerPageOptions={[10, 15, 25, 50]}
                variant="green"
                slotStart={selected.length > 0 && (
                  <span style={{ fontSize: 12, color: T.muted, marginRight: 4 }}>
                    <span style={{ color: T.green, fontWeight: 600 }}>{selected.length} selected</span>
                    <span style={{ marginLeft: 4 }}>·</span>
                  </span>
                )}
                sx={{ justifyContent: 'space-between', width: '100%' }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadsListViewV2;
